// TanStack Query mutations.
//
// Each mutation invalidates the queries that should re-fetch on success so
// any component reading those queries (UserMenu, profile, lobby, header,
// home) re-renders without manual cache plumbing.
import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { api, type Me } from "./api";
import {
  gameQuery,
  meQuery,
  type Game,
} from "./queries";

// Predicate-style invalidation: nukes every query under ["game", gameId, ...].
// Lighter to invoke than enumerating each query key, and keeps mutations
// honest as new game-scoped queries are added (scoreboard, round, etc.).
function invalidateGame(qc: QueryClient, gameId: string) {
  qc.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      return Array.isArray(k) && k[0] === "game" && k[1] === gameId;
    },
  });
}

interface AuthResponse {
  user: Me["user"];
}

// --- Auth -----------------------------------------------------------------

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      api.post<AuthResponse>("/api/v1/auth/login", { email }),
    onSuccess: () => qc.invalidateQueries({ queryKey: meQuery.queryKey }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<null>("/api/v1/auth/logout"),
    onSuccess: () => qc.invalidateQueries({ queryKey: meQuery.queryKey }),
  });
}

export function useAttachEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      api.patch<AuthResponse>("/api/v1/me/email", { email }),
    onSuccess: () => qc.invalidateQueries({ queryKey: meQuery.queryKey }),
  });
}

// --- Games ----------------------------------------------------------------

export interface CreateGameInput {
  name?: string;
  rounds: number;
  rules: { doubleBlankPenalty: boolean };
}

export interface CreateGameResponse {
  game: Game;
  joinToken: string;
  youAre: string;
}

export function useCreateGame() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGameInput) =>
      api.post<CreateGameResponse>("/api/v1/games", {
        name: input.name,
        rounds: input.rounds,
        rules: input.rules,
      }),
    onSuccess: ({ game, joinToken }) => {
      // Seed the lobby loader's cache so the navigate hits warm data.
      qc.setQueryData(gameQuery(game.id).queryKey, game);
      // The lobby reads this to render the join URL. If the host refreshes
      // the page, this drops; the lobby falls back to the lobby URL.
      qc.setQueryData(["game", game.id, "joinToken"] as const, joinToken);
      qc.invalidateQueries({ queryKey: meQuery.queryKey });
    },
  });
}

export function useStartGame(gameId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Game>(`/api/v1/games/${gameId}/start`),
    onSuccess: (game) => {
      qc.setQueryData(gameQuery(gameId).queryKey, game);
      invalidateGame(qc, gameId);
    },
  });
}

export interface JoinGameInput {
  name: string;
  email?: string;
}

export interface JoinGameResponse {
  gameId: string;
  playerId: string;
}

export function useJoinGame(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: JoinGameInput) =>
      api.post<JoinGameResponse>(`/api/v1/join/${token}`, {
        name: input.name,
        email: input.email && input.email.trim() ? input.email.trim() : undefined,
      }),
    onSuccess: ({ gameId }) => {
      qc.invalidateQueries({ queryKey: meQuery.queryKey });
      invalidateGame(qc, gameId);
    },
  });
}

// --- Round play & conductor ----------------------------------------------

export interface SubmitScoreInput {
  total: number;
}

export function useSubmitScore(gameId: string, n: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SubmitScoreInput) =>
      api.post<null>(`/api/v1/games/${gameId}/rounds/${n}/scores`, {
        total: input.total,
      }),
    onSuccess: () => invalidateGame(qc, gameId),
  });
}

export interface SubmitScoreWithPhotoInput {
  total: number;
  /** YOLO detections in INPUT_SIZE (640) space. */
  breakdown?: Array<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    score: number;
    cls: number;
  }>;
  photo: Blob;
}

/**
 * multipart/form-data score submit with optional ML breakdown + the
 * captured JPEG. Backend stores the photo at /static/scores/:scoreId.jpg
 * and serves it via /api/v1/games/:id/scores/:scoreId/photo.
 */
export function useSubmitScoreWithPhoto(gameId: string, n: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SubmitScoreWithPhotoInput) => {
      const fd = new FormData();
      fd.set("total", String(input.total));
      if (input.breakdown) {
        fd.set("breakdown", JSON.stringify(input.breakdown));
      }
      fd.set("photo", input.photo, "score.jpg");
      return api.postFormData<null>(
        `/api/v1/games/${gameId}/rounds/${n}/scores`,
        fd,
      );
    },
    onSuccess: () => invalidateGame(qc, gameId),
  });
}

export interface SetScoreInput {
  total: number;
  note?: string;
}

/** Host-only fill-in / audit. */
export function useSetScore(gameId: string, playerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SetScoreInput) =>
      api.post<null>(
        `/api/v1/games/${gameId}/players/${playerId}/score`,
        {
          total: input.total,
          ...(input.note ? { note: input.note } : {}),
        },
      ),
    onSuccess: () => invalidateGame(qc, gameId),
  });
}

/**
 * Apply an audit to a score. Same backend path as `useSetScore` but
 * scoped to the audit page — invalidates the score-detail query so the
 * audit log re-renders with the new entry, and the scoreboard so the cell
 * total updates.
 */
export interface ApplyAuditInput {
  total: number;
  note?: string;
}

export function useApplyAudit(
  gameId: string,
  playerId: string,
  scoreId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ApplyAuditInput) =>
      api.post<null>(
        `/api/v1/games/${gameId}/players/${playerId}/score`,
        {
          total: input.total,
          ...(input.note && input.note.trim()
            ? { note: input.note.trim() }
            : {}),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["game", gameId, "score", scoreId],
      });
      qc.invalidateQueries({ queryKey: ["game", gameId, "scoreboard"] });
    },
  });
}

// Conductor endpoints (advance/back/skip/finish) return
// `{game: Game, finished: boolean}` on the wire. Unwrap to the
// `game` object here so call sites read `next.status` /
// `next.currentRound` directly. The redundant `finished` flag is
// dropped — `game.status === "finished"` carries the same signal.
interface ConductorResponse {
  game: Game;
  finished: boolean;
}
function conductor(gameId: string, action: "advance" | "back" | "skip" | "finish") {
  return async () => {
    const res = await api.post<ConductorResponse>(
      `/api/v1/games/${gameId}/${action}`,
    );
    return res.game;
  };
}

export function useAdvance(gameId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: conductor(gameId, "advance"),
    onSuccess: (game) => {
      qc.setQueryData(gameQuery(gameId).queryKey, game);
      invalidateGame(qc, gameId);
    },
  });
}

export function useBack(gameId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: conductor(gameId, "back"),
    onSuccess: (game) => {
      qc.setQueryData(gameQuery(gameId).queryKey, game);
      invalidateGame(qc, gameId);
    },
  });
}

export function useSkip(gameId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: conductor(gameId, "skip"),
    onSuccess: (game) => {
      qc.setQueryData(gameQuery(gameId).queryKey, game);
      invalidateGame(qc, gameId);
    },
  });
}

export function useFinish(gameId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: conductor(gameId, "finish"),
    onSuccess: (game) => {
      qc.setQueryData(gameQuery(gameId).queryKey, game);
      invalidateGame(qc, gameId);
    },
  });
}
